// DEPENDENCIES
import React, { useState, useContext } from "react";

// CSS
import "./NewRoleDialog.css";

// MSAL
import { useMsal } from "@azure/msal-react";

// API IMPORTS
import { postAPICall } from "../../../../config/apiCalls";

// CONTEXT
import { UserContext } from "../../../../context/UserContext";

// MUI COMPONENTS
import {
	Alert,
	Autocomplete,
	Button,
	CircularProgress,
	Collapse,
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const NewRoleDialog = (props) => {
	const { instance, accounts } = useMsal();
	const employeeID = useContext(UserContext);

	const [roleName, setRoleName] = useState("");
	const [selectedTools, setSelectedTools] = useState([]);
	const [selectedAdminTools, setSelectedAdminTools] = useState([]);

	const [isLoading, setIsLoading] = useState(false);
	const [alertOpen, setAlertOpen] = useState(false);
	const [alertMessage, setAlertMessage] = useState("");

	const openAlert = (message) => {
		setAlertMessage(message);
		setAlertOpen(true);
	};

	const closeAlert = () => {
		setAlertOpen(false);
		setAlertMessage("");
	};

	const resetForm = () => {
		setRoleName("");
		setSelectedTools([]);
		setSelectedAdminTools([]);
		closeAlert();
	};

	const formClosed = () => {
		resetForm();
		props.newRoleDialogClosed();
	};

	const validateInputFields = () => {
		if (!roleName.trim()) {
			openAlert("Please enter a role name.");
			return false;
		}

		if (
			selectedTools.some((t) =>
				selectedAdminTools.map((a) => a.id).includes(t.id)
			)
		) {
			openAlert("Tools and Tools w/ Admin Access cannot have common elements.");
			return false;
		}

		return true;
	};

	const submitButtonClicked = async () => {
		if (!validateInputFields()) return;

		setIsLoading(true);
		try {
			const res = await postAPICall(
				instance,
				accounts[0],
				"/api/roles/create",
				{
					name: roleName.trim(),
					modifiedBy: employeeID,
				}
			);

			const newRoleId = res.data.insertId;

			for (const tool of selectedTools) {
				await postAPICall(instance, accounts[0], "/api/roles/tools/add", {
					roleID: newRoleId,
					toolID: tool.id,
					modifiedBy: employeeID,
				});
			}

			for (const tool of selectedAdminTools) {
				await postAPICall(instance, accounts[0], "/api/roles/tools/admin/add", {
					roleID: newRoleId,
					toolID: tool.id,
					modifiedBy: employeeID,
				});
			}

			props.fetchRoles();
			formClosed();
		} catch (err) {
			console.error("Error creating role:", err);
			openAlert("An error occurred. Please try again.");
		}
		setIsLoading(false);
	};

	return (
		<Dialog
			id="newRoleDialog"
			open={props.open}
			fullWidth
			onClose={formClosed}
		>
			<DialogTitle id="newRoleDialogTitle">New Role</DialogTitle>
			<DialogContent id="newRoleDialogContent">
				{isLoading && (
					<div className="loadingSpinnerContainer">
						<CircularProgress color="primary" />
					</div>
				)}
				{!isLoading && (
					<>
						<div className="newRoleDialogRow">
							<Collapse sx={{ flex: 1 }} in={alertOpen}>
								<Alert
									severity="error"
									action={
										<IconButton
											aria-label="close"
											color="inherit"
											size="small"
											onClick={closeAlert}
										>
											<CloseIcon fontSize="inherit" />
										</IconButton>
									}
								>
									{alertMessage}
								</Alert>
							</Collapse>
						</div>
						<div className="newRoleDialogRow">
							<TextField
								sx={{ flex: 1 }}
								label="Role Name"
								value={roleName}
								onChange={(e) => setRoleName(e.target.value)}
							/>
						</div>
						<div className="newRoleDialogRow">
							<Autocomplete
								sx={{ flex: 1 }}
								limitTags={2}
								multiple
								value={selectedTools}
								onChange={(_, newValue) => setSelectedTools(newValue)}
								options={props.tools.filter(
									(t) => !selectedAdminTools.map((a) => a.id).includes(t.id)
								)}
								getOptionLabel={(option) => option.name}
								filterSelectedOptions
								renderInput={(params) => (
									<TextField {...params} label="Tools" placeholder="Tools" />
								)}
							/>
						</div>
						<div className="newRoleDialogRow">
							<Autocomplete
								sx={{ flex: 1 }}
								limitTags={2}
								multiple
								value={selectedAdminTools}
								onChange={(_, newValue) => setSelectedAdminTools(newValue)}
								options={props.tools.filter(
									(t) => !selectedTools.map((s) => s.id).includes(t.id)
								)}
								getOptionLabel={(option) => option.name}
								filterSelectedOptions
								renderInput={(params) => (
									<TextField
										{...params}
										label="Tools w/ Admin Access"
										placeholder="Tools"
									/>
								)}
							/>
						</div>
					</>
				)}
				<div className="dialogButtons">
					<Button onClick={formClosed} variant="outlined" color="error">
						Cancel
					</Button>
					<Button onClick={submitButtonClicked} variant="contained">
						Submit
					</Button>
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default NewRoleDialog;
